// Foresight Studio — the Analytics tab's dedicated ML workspace (obj_type F).
//
// List view: card grid of Foresight models (task badge + last-run metric).
// Studio view: a 4-step workspace over one model —
//   1. Task & training data (table/SQL source + criteria + live sample)
//   2. Setup (forecast: date/bucket/measure/agg · predict: target + features)
//   3. Parameters (horizon/confidence/backtest · holdout/task type)
//   4. Run & results (run history, forecast chart with band via the shared
//      visual engine, prediction metrics/drivers/scored rows, materialize)
//
// Forecast runs complete synchronously; predict runs are ML-sidecar jobs the
// section polls until completion.
import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { SqlBuilderButton } from '@twasta/page-runtime/designerKit'
import { InsightVisual } from '@twasta/visual-engine'

import {
  deleteForesight,
  getForesight,
  getForesightRun,
  listForesight,
  listForesightColumns,
  listForesightRuns,
  listForesightTables,
  materializeForesightRun,
  previewForesightSource,
  runForesight,
  saveForesight,
} from '@/api/foresight'
import type {
  ForesightColumn,
  ForesightModel,
  ForesightRun,
  ForesightRunSummary,
  ForesightSummary,
  ForesightTask,
} from '@/api/foresight'
import { MicroBadge, cardStyle, eyebrowStyle } from './shared'

const inputStyle: React.CSSProperties = {
  padding: '7px 10px',
  borderRadius: 8,
  border: '1px solid var(--color-border-strong)',
  background: 'var(--color-input-bg)',
  color: 'var(--color-text)',
  fontSize: 14.5,
  width: '100%',
  boxSizing: 'border-box',
}

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  marginBottom: 4,
  display: 'block',
}

function Btn({
  children, onClick, primary, danger, disabled, small,
}: {
  children: React.ReactNode
  onClick?: () => void
  primary?: boolean
  danger?: boolean
  disabled?: boolean
  small?: boolean
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: small ? '5px 11px' : '8px 16px',
        borderRadius: 8,
        fontSize: small ? 12.5 : 13.5,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        border: primary || danger ? 'none' : '1px solid var(--color-border)',
        background: primary
          ? 'var(--color-accent)'
          : danger
            ? 'var(--color-error)'
            : 'var(--color-bg-secondary)',
        color: primary || danger ? '#fff' : 'var(--color-text)',
      }}
    >
      {children}
    </button>
  )
}

function emptyModel(name: string, task: ForesightTask): ForesightModel {
  return {
    FORESIGHT: {
      NAME: name,
      DESCRIPTION: '',
      TASK: task,
      SOURCE: { MODE: 'table', TABLE: '', SQL: '', CRITERIA: '' },
      SERIES: { DATE_COLUMN: '', BUCKET: 'month', MEASURE: '', AGG: 'SUM', FREQUENCY: 'auto' },
      TARGET: { COLUMN: '', FEATURES: [], TASK_TYPE: 'auto' },
      PARAMS: { HORIZON: 0, CONFIDENCE: 0.8, BACKTEST: true, HOLDOUT_FRACTION: 0.2 },
    },
  }
}

/** Build the shared-engine payload for a completed forecast run — reuses the
 *  LineChart forecast styling (dashed continuation + band) end to end. */
function forecastRunToVisual(run: ForesightRun) {
  const r = run.result
  if (!r?.history || !r.forecast) return null
  const rows: Record<string, unknown>[] = []
  const n = r.history.timestamps.length
  for (let i = 0; i < n; i++) {
    const row: Record<string, unknown> = {
      id: i + 1, BUCKET: r.history.timestamps[i], ACTUAL: r.history.values[i],
    }
    if (i === n - 1) {
      row.ACTUAL__FORECAST = r.history.values[i]
      row.ACTUAL__FORECAST_LO = r.history.values[i]
      row.ACTUAL__FORECAST_HI = r.history.values[i]
    }
    rows.push(row)
  }
  r.forecast.timestamps.forEach((t, i) => {
    rows.push({
      id: n + i + 1,
      BUCKET: t,
      ACTUAL__FORECAST: r.forecast!.yhat[i],
      ACTUAL__FORECAST_LO: r.forecast!.lo[i],
      ACTUAL__FORECAST_HI: r.forecast!.hi[i],
    })
  })
  const schema: Record<string, unknown> = {
    id: 0,
    BUCKET: { type: 'datetime' },
    ACTUAL: { type: 'number' },
    ACTUAL__FORECAST: { type: 'number' },
    ACTUAL__FORECAST_LO: { type: 'number' },
    ACTUAL__FORECAST_HI: { type: 'number' },
  }
  const visualLayout = {
    ID: 'line',
    VisualName: 'Forecast',
    className: 'InsightLineChart',
    OutputType: 'JSON',
    options: [],
    ColumnGroups: [
      { StandardName: 'Rows', COLUMNS: [{ NAME: 'BUCKET', content: 'Period' }] },
      {
        StandardName: 'Values',
        COLUMNS: [
          { NAME: 'ACTUAL', content: 'Actual', COLTYPE: 'NUMBER' },
          { NAME: 'ACTUAL__FORECAST', content: 'Forecast', COLTYPE: 'NUMBER', IS_FORECAST: true },
          { NAME: 'ACTUAL__FORECAST_LO', content: 'Lower', COLTYPE: 'NUMBER' },
          { NAME: 'ACTUAL__FORECAST_HI', content: 'Upper', COLTYPE: 'NUMBER' },
        ],
      },
      { StandardName: 'Columns', COLUMNS: [] },
    ],
  }
  return { jsonData: [schema, ...rows], visualLayout }
}

function statusTone(status?: string): 'success' | 'warn' | 'error' | 'muted' {
  if (status === 'completed') return 'success'
  if (status === 'running') return 'warn'
  if (status === 'failed') return 'error'
  return 'muted'
}

function fmtMetric(m?: Record<string, number | string | null>): string {
  if (!m) return ''
  if (m.mape != null) return `±${(Number(m.mape) * 100).toFixed(1)}% MAPE`
  if (m.auc != null) return `AUC ${Number(m.auc).toFixed(2)}`
  if (m.accuracy != null) return `${(Number(m.accuracy) * 100).toFixed(0)}% accuracy`
  if (m.r2 != null) return `R² ${Number(m.r2).toFixed(2)}`
  if (m.mae != null) return `MAE ${Number(m.mae).toFixed(2)}`
  return ''
}

export default function ForesightSection({ project }: { project: string }) {
  const [models, setModels] = useState<ForesightSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<'list' | 'studio'>('list')
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newTask, setNewTask] = useState<ForesightTask>('forecast')

  // Studio state
  const [objName, setObjName] = useState('')
  const [model, setModel] = useState<ForesightModel | null>(null)
  const [dirty, setDirty] = useState(false)
  const [tables, setTables] = useState<string[]>([])
  const [columns, setColumns] = useState<ForesightColumn[]>([])
  const [sample, setSample] = useState<{ columns: string[]; rows: Record<string, unknown>[] } | null>(null)
  const [sampleLoading, setSampleLoading] = useState(false)
  const [runs, setRuns] = useState<ForesightRunSummary[]>([])
  const [runDetail, setRunDetail] = useState<ForesightRun | null>(null)
  const [running, setRunning] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fs = model?.FORESIGHT

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      setModels(await listForesight(project))
    } catch {
      toast.error('Could not load Foresight models')
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { void reload() }, [reload])

  // ── studio helpers ──────────────────────────────────────────────────────

  const patch = (updater: (f: ForesightModel['FORESIGHT']) => void) => {
    setModel((m) => {
      if (!m) return m
      const next = JSON.parse(JSON.stringify(m)) as ForesightModel
      updater(next.FORESIGHT)
      return next
    })
    setDirty(true)
  }

  const openStudio = async (name: string, existing: boolean, task?: ForesightTask) => {
    try {
      const m = existing ? await getForesight(project, name) : emptyModel(name, task || 'forecast')
      setObjName(name)
      setModel(m)
      setDirty(!existing)
      setSample(null)
      setRunDetail(null)
      setView('studio')
      setRuns(existing ? await listForesightRuns(project, name) : [])
      setTables(await listForesightTables(project))
      const t = m.FORESIGHT.SOURCE?.TABLE
      if (t) setColumns(await listForesightColumns(project, t))
      else setColumns([])
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not open the model')
    }
  }

  const onPickTable = async (t: string) => {
    patch((f) => { f.SOURCE.TABLE = t })
    setColumns(t ? await listForesightColumns(project, t).catch(() => []) : [])
  }

  const doSave = async (): Promise<boolean> => {
    if (!model) return false
    try {
      await saveForesight(project, objName, model)
      setDirty(false)
      return true
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Save failed')
      return false
    }
  }

  const doPreview = async () => {
    if (!model) return
    setSampleLoading(true)
    try {
      setSample(await previewForesightSource(project, model))
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Preview failed')
    } finally {
      setSampleLoading(false)
    }
  }

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
  }

  const startPolling = useCallback((runId: string) => {
    stopPolling()
    pollRef.current = setInterval(async () => {
      try {
        const r = await getForesightRun(project, objName, runId)
        setRunDetail(r)
        if (r.status !== 'running') {
          stopPolling()
          setRuns(await listForesightRuns(project, objName))
          if (r.status === 'completed') toast.success('Run completed')
          else if (r.status === 'failed') toast.error(r.error || 'Run failed')
        }
      } catch {
        // transient poll error — keep trying; the backend reports poll_error
      }
    }, 2500)
  }, [project, objName])

  useEffect(() => () => stopPolling(), [])

  const doRun = async () => {
    if (!model) return
    if (dirty && !(await doSave())) return
    setRunning(true)
    try {
      const run = await runForesight(project, objName)
      setRunDetail(run)
      setRuns(await listForesightRuns(project, objName))
      if (run.status === 'running') startPolling(run.run_id)
      else if (run.status === 'failed') toast.error(run.error || 'Run failed')
      else toast.success('Run completed')
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Run failed')
    } finally {
      setRunning(false)
    }
  }

  const openRun = async (runId: string) => {
    try {
      const r = await getForesightRun(project, objName, runId)
      setRunDetail(r)
      if (r.status === 'running') startPolling(runId)
    } catch {
      toast.error('Could not load the run')
    }
  }

  const doMaterialize = async () => {
    if (!runDetail) return
    try {
      const out = await materializeForesightRun(project, objName, runDetail.run_id)
      toast.success(`Saved ${out.rows} rows to table ${out.table}`)
      setRunDetail(await getForesightRun(project, objName, runDetail.run_id))
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Materialize failed')
    }
  }

  const doDelete = async (name: string) => {
    if (!window.confirm(`Delete Foresight model "${name}" and its run history?`)) return
    try {
      await deleteForesight(project, name)
      toast.success('Deleted')
      void reload()
    } catch {
      toast.error('Delete failed')
    }
  }

  const dateColumns = columns.filter((c) => c.type === 'date')
  const numberColumns = columns.filter((c) => c.type === 'number')

  // ── list view ───────────────────────────────────────────────────────────

  if (view === 'list') {
    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <div>
            <div style={eyebrowStyle}>Foresight Studio</div>
            <h2 style={{ margin: '4px 0 2px', fontSize: 21 }}>Forecasting & Prediction</h2>
            <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
              Train-free time-series forecasts and predict-a-column experiments over this
              project's data. Results can be saved as tables for visuals and dashboards.
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <Btn primary onClick={() => setCreating(true)}>New Foresight</Btn>
        </div>

        {creating && (
          <div style={{ ...cardStyle, padding: 16, margin: '14px 0', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 220 }}>
              <label style={labelStyle}>Name</label>
              <input
                style={inputStyle}
                value={newName}
                placeholder="e.g. SALES_FORECAST"
                onChange={(e) => setNewName(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))}
              />
            </div>
            <div style={{ minWidth: 200 }}>
              <label style={labelStyle}>Task</label>
              <select style={inputStyle} value={newTask} onChange={(e) => setNewTask(e.target.value as ForesightTask)}>
                <option value="forecast">Forecast a measure over time</option>
                <option value="predict">Predict a column</option>
              </select>
            </div>
            <Btn
              primary
              disabled={!newName.trim()}
              onClick={() => {
                setCreating(false)
                void openStudio(newName.trim(), false, newTask)
                setNewName('')
              }}
            >
              Create
            </Btn>
            <Btn onClick={() => setCreating(false)}>Cancel</Btn>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 40, color: 'var(--color-text-secondary)' }}>Loading…</div>
        ) : models.length === 0 && !creating ? (
          <div style={{ ...cardStyle, padding: 40, marginTop: 16, textAlign: 'center', color: 'var(--color-text-secondary)' }}>
            No Foresight models yet. Create one to forecast a measure or predict a column —
            no ML setup needed.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 14, marginTop: 16 }}>
            {models.map((m) => (
              <div key={m.obj_name} style={{ ...cardStyle, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    aria-hidden
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 20, height: 20, borderRadius: 5, background: '#d29922',
                      color: '#fff', fontWeight: 700, fontSize: 13,
                    }}
                  >
                    F
                  </span>
                  <strong style={{ fontSize: 15.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.name}
                  </strong>
                  <div style={{ flex: 1 }} />
                  <MicroBadge text={m.task === 'forecast' ? 'Forecast' : 'Predict'} tone="accent" />
                </div>
                {m.description && (
                  <div style={{ fontSize: 13.5, color: 'var(--color-text-secondary)' }}>{m.description}</div>
                )}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 22 }}>
                  {m.last_run ? (
                    <>
                      <MicroBadge text={m.last_run.status} tone={statusTone(m.last_run.status)} />
                      {fmtMetric(m.last_run.metrics ?? undefined) && (
                        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                          {fmtMetric(m.last_run.metrics ?? undefined)}
                        </span>
                      )}
                    </>
                  ) : (
                    <span style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>Never run</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <Btn small primary onClick={() => void openStudio(m.obj_name, true)}>Open studio</Btn>
                  <Btn small danger onClick={() => void doDelete(m.obj_name)}>Delete</Btn>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // ── studio view ─────────────────────────────────────────────────────────

  if (!fs) return null
  const isForecast = fs.TASK === 'forecast'
  const chart = runDetail?.status === 'completed' && isForecast ? forecastRunToVisual(runDetail) : null

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Btn small onClick={() => { setView('list'); stopPolling(); void reload() }}>← Back</Btn>
        <h2 style={{ margin: 0, fontSize: 20 }}>{fs.NAME || objName}</h2>
        <MicroBadge text={isForecast ? 'Forecast' : 'Predict'} tone="accent" />
        {dirty && <MicroBadge text="unsaved" tone="warn" />}
        <div style={{ flex: 1 }} />
        <Btn small onClick={() => void doSave()} disabled={!dirty}>Save</Btn>
        <Btn small primary onClick={() => void doRun()} disabled={running}>
          {running ? 'Starting…' : '▶ Run'}
        </Btn>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(340px, 430px) 1fr', gap: 18, alignItems: 'start' }}>
        {/* ── Left: steps ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Step 1 — training data */}
          <div style={{ ...cardStyle, padding: 16 }}>
            <div style={eyebrowStyle}>Step 1 · Training data</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
              <div>
                <label style={labelStyle}>Description</label>
                <input
                  style={inputStyle}
                  value={fs.DESCRIPTION || ''}
                  placeholder="What does this model do?"
                  onChange={(e) => patch((f) => { f.DESCRIPTION = e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle}>Source</label>
                <select
                  style={inputStyle}
                  value={fs.SOURCE.MODE}
                  onChange={(e) => patch((f) => { f.SOURCE.MODE = e.target.value as 'table' | 'sql' })}
                >
                  <option value="table">Project table</option>
                  <option value="sql">Custom SQL</option>
                </select>
              </div>
              {fs.SOURCE.MODE === 'table' ? (
                <>
                  <div>
                    <label style={labelStyle}>Table</label>
                    <select style={inputStyle} value={fs.SOURCE.TABLE || ''} onChange={(e) => void onPickTable(e.target.value)}>
                      <option value="">— choose a table —</option>
                      {tables.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                      <label style={labelStyle}>Filter rows (optional WHERE clause)</label>
                      {fs.SOURCE.TABLE && (
                        <SqlBuilderButton value={fs.SOURCE.CRITERIA || ''}
                          onChange={(v) => patch((f) => { f.SOURCE.CRITERIA = v })}
                          mode="where" contextTables={[fs.SOURCE.TABLE]} project={project} compact
                          title={`Rows of ${fs.SOURCE.TABLE} to learn from`} />
                      )}
                    </div>
                    <input
                      style={inputStyle}
                      value={fs.SOURCE.CRITERIA || ''}
                      placeholder="e.g. status = 'PAID' AND amount > 0"
                      onChange={(e) => patch((f) => { f.SOURCE.CRITERIA = e.target.value })}
                    />
                  </div>
                </>
              ) : (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                    <label style={labelStyle}>
                      SQL {isForecast ? '(must return a date column + a numeric column)' : '(features + target column)'}
                    </label>
                    <SqlBuilderButton value={fs.SOURCE.SQL || ''}
                      onChange={(v) => patch((f) => { f.SOURCE.SQL = v })}
                      project={project} title="Foresight source" />
                  </div>
                  <textarea
                    style={{ ...inputStyle, minHeight: 90, fontFamily: 'monospace', fontSize: 13.5 }}
                    value={fs.SOURCE.SQL || ''}
                    onChange={(e) => patch((f) => { f.SOURCE.SQL = e.target.value })}
                  />
                </div>
              )}
              <div>
                <Btn small onClick={() => void doPreview()} disabled={sampleLoading}>
                  {sampleLoading ? 'Loading…' : 'Preview data'}
                </Btn>
              </div>
            </div>
          </div>

          {/* Step 2 — setup */}
          <div style={{ ...cardStyle, padding: 16 }}>
            <div style={eyebrowStyle}>Step 2 · {isForecast ? 'Series setup' : 'Prediction setup'}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
              {isForecast ? (
                fs.SOURCE.MODE === 'sql' ? (
                  <div style={{ fontSize: 13.5, color: 'var(--color-text-secondary)' }}>
                    Custom SQL mode: the first date column and first numeric column of the
                    result are used as the series automatically.
                  </div>
                ) : (
                  <>
                    <div>
                      <label style={labelStyle}>Date column</label>
                      <select
                        style={inputStyle}
                        value={fs.SERIES?.DATE_COLUMN || ''}
                        onChange={(e) => patch((f) => { f.SERIES = { ...f.SERIES, DATE_COLUMN: e.target.value } })}
                      >
                        <option value="">— choose —</option>
                        {dateColumns.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                      </select>
                      {fs.SOURCE.TABLE && dateColumns.length === 0 && (
                        <div style={{ fontSize: 13, color: 'var(--color-warning)', marginTop: 4 }}>
                          This table has no date column — forecasting needs one.
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div>
                        <label style={labelStyle}>Group into</label>
                        <select
                          style={inputStyle}
                          value={fs.SERIES?.BUCKET || 'month'}
                          onChange={(e) => patch((f) => { f.SERIES = { ...f.SERIES, BUCKET: e.target.value as NonNullable<ForesightModel['FORESIGHT']['SERIES']>['BUCKET'] } })}
                        >
                          {['day', 'week', 'month', 'quarter', 'year'].map((b) => (
                            <option key={b} value={b}>{b[0].toUpperCase() + b.slice(1)} buckets</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>Aggregation</label>
                        <select
                          style={inputStyle}
                          value={fs.SERIES?.AGG || 'SUM'}
                          onChange={(e) => patch((f) => { f.SERIES = { ...f.SERIES, AGG: e.target.value as NonNullable<ForesightModel['FORESIGHT']['SERIES']>['AGG'] } })}
                        >
                          {['SUM', 'AVG', 'COUNT', 'MIN', 'MAX'].map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label style={labelStyle}>Measure</label>
                      <select
                        style={inputStyle}
                        value={fs.SERIES?.MEASURE || ''}
                        onChange={(e) => patch((f) => { f.SERIES = { ...f.SERIES, MEASURE: e.target.value } })}
                      >
                        <option value="">— choose —</option>
                        {numberColumns.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                      </select>
                    </div>
                  </>
                )
              ) : (
                <>
                  <div>
                    <label style={labelStyle}>Target column (what to predict)</label>
                    <select
                      style={inputStyle}
                      value={fs.TARGET?.COLUMN || ''}
                      onChange={(e) => patch((f) => { f.TARGET = { ...f.TARGET, COLUMN: e.target.value } })}
                    >
                      <option value="">— choose —</option>
                      {columns.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                    </select>
                    <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                      Rows where this column has a value become training data; empty rows get predictions.
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Feature columns (empty = all other columns)</label>
                    <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 8, padding: 8 }}>
                      {columns.filter((c) => c.name !== fs.TARGET?.COLUMN).map((c) => {
                        const selected = (fs.TARGET?.FEATURES || []).includes(c.name)
                        return (
                          <label key={c.name} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, padding: '3px 2px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => patch((f) => {
                                const cur = new Set(f.TARGET?.FEATURES || [])
                                if (cur.has(c.name)) cur.delete(c.name)
                                else cur.add(c.name)
                                f.TARGET = { ...f.TARGET, FEATURES: [...cur] }
                              })}
                            />
                            {c.name}
                            <span style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>{c.type}</span>
                          </label>
                        )
                      })}
                      {columns.length === 0 && (
                        <div style={{ fontSize: 13.5, color: 'var(--color-text-faint)' }}>Pick a table first.</div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Step 3 — parameters */}
          <div style={{ ...cardStyle, padding: 16 }}>
            <div style={eyebrowStyle}>Step 3 · Parameters</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
              {isForecast ? (
                <>
                  <div>
                    <label style={labelStyle}>Horizon (0 = auto)</label>
                    <input
                      type="number" min={0} max={500} style={inputStyle}
                      value={fs.PARAMS?.HORIZON ?? 0}
                      onChange={(e) => patch((f) => { f.PARAMS = { ...f.PARAMS, HORIZON: Number(e.target.value) || 0 } })}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Confidence band</label>
                    <select
                      style={inputStyle}
                      value={String(fs.PARAMS?.CONFIDENCE ?? 0.8)}
                      onChange={(e) => patch((f) => { f.PARAMS = { ...f.PARAMS, CONFIDENCE: Number(e.target.value) } })}
                    >
                      <option value="0.6">60%</option>
                      <option value="0.8">80%</option>
                      <option value="0.9">90%</option>
                    </select>
                  </div>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, gridColumn: '1 / -1' }}>
                    <input
                      type="checkbox"
                      checked={fs.PARAMS?.BACKTEST !== false}
                      onChange={(e) => patch((f) => { f.PARAMS = { ...f.PARAMS, BACKTEST: e.target.checked } })}
                    />
                    Backtest on held-out history (accuracy strip)
                  </label>
                </>
              ) : (
                <>
                  <div>
                    <label style={labelStyle}>Holdout fraction</label>
                    <select
                      style={inputStyle}
                      value={String(fs.PARAMS?.HOLDOUT_FRACTION ?? 0.2)}
                      onChange={(e) => patch((f) => { f.PARAMS = { ...f.PARAMS, HOLDOUT_FRACTION: Number(e.target.value) } })}
                    >
                      <option value="0.1">10%</option>
                      <option value="0.2">20%</option>
                      <option value="0.3">30%</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Task type</label>
                    <select
                      style={inputStyle}
                      value={fs.TARGET?.TASK_TYPE || 'auto'}
                      onChange={(e) => patch((f) => { f.TARGET = { ...f.TARGET, TASK_TYPE: e.target.value } })}
                    >
                      <option value="auto">Auto-detect</option>
                      <option value="classification">Classification</option>
                      <option value="regression">Regression</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Run history */}
          <div style={{ ...cardStyle, padding: 16 }}>
            <div style={eyebrowStyle}>Run history</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
              {runs.length === 0 && (
                <div style={{ fontSize: 13.5, color: 'var(--color-text-faint)' }}>No runs yet.</div>
              )}
              {runs.map((r) => (
                <button
                  key={r.run_id}
                  onClick={() => void openRun(r.run_id)}
                  style={{
                    display: 'flex', gap: 8, alignItems: 'center', padding: '7px 10px',
                    borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                    border: '1px solid var(--color-border)',
                    background: runDetail?.run_id === r.run_id ? 'var(--color-accent-weak)' : 'transparent',
                    color: 'var(--color-text)',
                    fontSize: 13.5,
                  }}
                >
                  <MicroBadge text={r.status} tone={statusTone(r.status)} />
                  <span style={{ color: 'var(--color-text-secondary)' }}>
                    {(r.started_at || '').replace('T', ' ').slice(0, 16)}
                  </span>
                  <div style={{ flex: 1 }} />
                  <span>{fmtMetric(r.metrics ?? undefined)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Right: sample + results ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          {sample && (
            <div style={{ ...cardStyle, padding: 16, overflow: 'hidden' }}>
              <div style={eyebrowStyle}>Data sample ({sample.rows.length} rows)</div>
              <div style={{ overflowX: 'auto', marginTop: 10 }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
                  <thead>
                    <tr>
                      {sample.columns.slice(0, 12).map((c) => (
                        <th key={c} style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sample.rows.slice(0, 8).map((r, i) => (
                      <tr key={i}>
                        {sample.columns.slice(0, 12).map((c) => (
                          <td key={c} style={{ padding: '4px 8px', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {String(r[c] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {runDetail ? (
            <div style={{ ...cardStyle, padding: 16, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={eyebrowStyle}>Result · run {runDetail.run_id}</div>
                <MicroBadge text={runDetail.status} tone={statusTone(runDetail.status)} />
                {runDetail.engine && <MicroBadge text={runDetail.engine} tone="muted" />}
                <div style={{ flex: 1 }} />
                {runDetail.status === 'completed' && (
                  <Btn small onClick={() => void doMaterialize()}>Save as table</Btn>
                )}
              </div>

              {runDetail.status === 'running' && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
                    Running… {runDetail.progress != null ? `${Math.round((runDetail.progress || 0) * 100)}%` : ''}
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: 'var(--color-surface-2)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.round((runDetail.progress || 0.05) * 100)}%`, background: 'var(--color-accent)', transition: 'width .4s' }} />
                  </div>
                </div>
              )}

              {runDetail.status === 'failed' && (
                <div style={{ marginTop: 12, fontSize: 14, color: 'var(--color-error)' }}>
                  {runDetail.error || 'The run failed.'}
                </div>
              )}

              {runDetail.status === 'completed' && (
                <>
                  {/* Metrics strip */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
                    {Object.entries(runDetail.metrics || {}).map(([k, v]) => (
                      <span key={k} style={{ fontSize: 13.5, padding: '4px 10px', borderRadius: 8, background: 'var(--color-surface-2)', color: 'var(--color-text)' }}>
                        <strong>{k}</strong>: {typeof v === 'number' ? (k === 'mape' ? `${(v * 100).toFixed(1)}%` : v) : String(v)}
                      </span>
                    ))}
                    {isForecast && runDetail.result?.narrative?.seasonality_periods != null && (
                      <span style={{ fontSize: 13.5, padding: '4px 10px', borderRadius: 8, background: 'var(--color-surface-2)' }}>
                        seasonality: every {String(runDetail.result.narrative.seasonality_periods)} periods
                      </span>
                    )}
                  </div>

                  {isForecast && runDetail.result?.backtest?.mape != null && (
                    <div style={{ fontSize: 13.5, color: 'var(--color-text-secondary)', marginBottom: 10 }}>
                      On the last {runDetail.result.backtest ? (runDetail.result.backtest as { actual?: unknown[] }).actual?.length ?? '' : ''} held-out
                      periods this forecast was typically within ±{((runDetail.result.backtest.mape || 0) * 100).toFixed(1)}% of the actuals.
                    </div>
                  )}

                  {/* Forecast chart (shared engine — dashed line + band) */}
                  {chart && (
                    <div style={{ height: 380 }}>
                      <InsightVisual
                        jsonData={chart.jsonData as never}
                        visualLayout={chart.visualLayout as never}
                        optionsArr={[]}
                      />
                    </div>
                  )}

                  {/* Predict results */}
                  {!isForecast && runDetail.result && (
                    <>
                      {(runDetail.result.drivers || []).length > 0 && (
                        <div style={{ margin: '6px 0 14px' }}>
                          <div style={{ ...eyebrowStyle, marginBottom: 8 }}>What drives {runDetail.result.target}</div>
                          {(runDetail.result.drivers || []).slice(0, 8).map((d) => (
                            <div key={d.feature} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                              <span style={{ width: 160, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.feature}</span>
                              <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--color-surface-2)' }}>
                                <div style={{ height: '100%', borderRadius: 4, width: `${Math.round(d.importance * 100)}%`, background: 'var(--color-accent)' }} />
                              </div>
                              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', width: 42, textAlign: 'right' }}>
                                {(d.importance * 100).toFixed(0)}%
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ ...eyebrowStyle, marginBottom: 8 }}>
                        Predictions ({runDetail.result.scored_count ?? 0} rows scored, trained on {runDetail.result.trained_count ?? 0})
                      </div>
                      {(runDetail.result.scored_rows || []).length === 0 ? (
                        <div style={{ fontSize: 13.5, color: 'var(--color-text-faint)' }}>
                          No rows needed scoring — every row already has a {runDetail.result.target} value.
                        </div>
                      ) : (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
                            <thead>
                              <tr>
                                {Object.keys((runDetail.result.scored_rows || [])[0] || {})
                                  .filter((k) => !k.startsWith('__'))
                                  .slice(0, 8)
                                  .map((c) => (
                                    <th key={c} style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{c}</th>
                                  ))}
                                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-accent)' }}>predicted</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>confidence</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(runDetail.result.scored_rows || []).slice(0, 50).map((r, i) => (
                                <tr key={i}>
                                  {Object.keys(r).filter((k) => !k.startsWith('__')).slice(0, 8).map((c) => (
                                    <td key={c} style={{ padding: '4px 8px', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {String(r[c] ?? '')}
                                    </td>
                                  ))}
                                  <td style={{ padding: '4px 8px', borderBottom: '1px solid var(--color-border)', fontWeight: 600, color: 'var(--color-accent)' }}>
                                    {String(r.__predicted ?? '')}
                                  </td>
                                  <td style={{ padding: '4px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>
                                    {r.__confidence != null ? `${(Number(r.__confidence) * 100).toFixed(0)}%` : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}

                  {(runDetail.materialized || []).length > 0 && (
                    <div style={{ marginTop: 12, fontSize: 13.5, color: 'var(--color-success)' }}>
                      Saved to table <code>{runDetail.materialized![runDetail.materialized!.length - 1].table}</code> —
                      usable in visuals, dashboards and schemas.
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div style={{ ...cardStyle, padding: 40, textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: 14.5 }}>
              Configure the steps on the left, then hit <strong>Run</strong>.
              Results (chart, accuracy, predictions) appear here; completed runs can be
              saved as a table for visuals and dashboards.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
